import { endOfDay, startOfDay, startOfQuarter } from "date-fns";
import { NetFlowsScale } from "~/modules/visualization/components/NetFlowsChart/component";
import { today } from "~/modules/flows_explorer/pages/list/DateRangePicker";
import { FlowsChartConfig, ChartConfig } from "~/modules/charts/types";
import {
    AggregateFunctions,
    AggregateQueryInterval,
    ApiQuery,
    Filter,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";

// // // //

export const NET_FLOWS_MAX_ASSETS = 200;

export const DEFAULT_NET_FLOWS_GROUP_BY = TokenTimeseriesGroupBy.network;

export const DEFAULT_NET_FLOWS_QUERY: ApiQuery = {
    filter: {
        operator: FilterOperators.And,
        filters: [],
    },
    aggregate: {
        groupBy: DEFAULT_NET_FLOWS_GROUP_BY,
        aggregateFunction: AggregateFunctions.Sum,
        interval: AggregateQueryInterval.day,
        groupLimit: NET_FLOWS_MAX_ASSETS,
    },
    sort: {
        direction: SortDirections.asc,
        field: TokenTimeseriesColumns.date,
    },
    pagination: {
        page: 1,
        perPage: 25,
    },
};

export const MIN_NET_VALUE_STEPS = [
    0, 1, 10, 100, 1_000, 10_000, 100_000, 1_000_000, 10_000_000, 100_000_000,
    1_000_000_000,
] as const;

export const DEFAULT_MIN_NET_FLOW = 1;

export const NET_FLOWS_SCALE_OPTIONS: {
    value: NetFlowsScale;
    label: string;
}[] = [
    { value: "linear", label: "Linear" },
    { value: "symlog", label: "Logarithmic" },
];

const DEFAULT_START_DATE = startOfDay(startOfQuarter(today()));
const DEFAULT_END_DATE = endOfDay(today());

export const DEFAULT_NET_FLOWS_CONFIG: ChartConfig = {
    start_date: DEFAULT_START_DATE.toISOString(),
    end_date: DEFAULT_END_DATE.toISOString(),
    min_net_flow: DEFAULT_MIN_NET_FLOW,
    scale: "linear",
    query: DEFAULT_NET_FLOWS_QUERY,
    default_group_by: DEFAULT_NET_FLOWS_GROUP_BY,
    groups_by_options: [],
};

const isValidScale = (value: unknown): value is NetFlowsScale => {
    return value === "linear" || value === "symlog";
};

const parseAssetIds = (value: unknown): number[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((entry) => Number(entry))
        .filter((id) => Number.isFinite(id) && id > 0);
};

const isGroupBy = (value: unknown): value is TokenTimeseriesGroupBy => {
    return Object.values(TokenTimeseriesGroupBy).includes(
        value as TokenTimeseriesGroupBy,
    );
};

const parseGroupBy = (value: unknown): TokenTimeseriesGroupBy => {
    return isGroupBy(value) ? value : DEFAULT_NET_FLOWS_GROUP_BY;
};

const parseGroupByOptions = (value: unknown): TokenTimeseriesGroupBy[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter(isGroupBy);
};

/**
 * parseQuery
 * Reads the stored query, migrating legacy `asset_ids` configs into an
 * equivalent assetID filter so previously-saved net flows charts still load.
 */
const parseQuery = (value: ChartConfig): ApiQuery => {
    const storedQuery = value.query;
    if (storedQuery && typeof storedQuery === "object") {
        return storedQuery as ApiQuery;
    }

    const assetIds = parseAssetIds(value.asset_ids);
    if (assetIds.length === 0) {
        return DEFAULT_NET_FLOWS_QUERY;
    }

    const assetFilter: Filter = {
        field: TokenTimeseriesColumns.assetID,
        operator: FilterOperators.In,
        value: assetIds,
    };

    return {
        ...DEFAULT_NET_FLOWS_QUERY,
        filter: {
            operator: FilterOperators.And,
            filters: [assetFilter],
        },
    };
};

const parseDateString = (value: unknown, fallback: Date): string => {
    if (typeof value !== "string" || value.trim().length === 0) {
        return fallback.toISOString();
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return fallback.toISOString();
    }

    return parsed.toISOString();
};

/**
 * getNetFlowsApiFilters
 * Converts a net flows query into the flat `Filter[]` accepted by
 * `queryFlowsFromDate`, returning undefined when the query has no conditions.
 */
export function getNetFlowsApiFilters(query: ApiQuery): Filter[] | undefined {
    const filter = query.filter;

    if (!filter) {
        return undefined;
    }

    const hasNoConditions =
        filter.operator === FilterOperators.And &&
        (filter.filters?.length ?? 0) === 0;

    if (hasNoConditions) {
        return undefined;
    }

    return [filter];
}

/**
 * toNetFlowsChartConfig
 * Normalizes a loose chart config object into a net flows config shape.
 */
export function toNetFlowsChartConfig(value: ChartConfig): FlowsChartConfig {
    return {
        start_date: parseDateString(value.start_date, DEFAULT_START_DATE),
        end_date: parseDateString(value.end_date, DEFAULT_END_DATE),
        min_net_flow:
            typeof value.min_net_flow === "number" &&
            Number.isFinite(value.min_net_flow) &&
            value.min_net_flow >= 0
                ? value.min_net_flow
                : DEFAULT_MIN_NET_FLOW,
        scale: isValidScale(value.scale) ? value.scale : "linear",
        query: parseQuery(value),
        default_group_by: parseGroupBy(value.default_group_by),
        groups_by_options: parseGroupByOptions(value.groups_by_options),
    };
}

/**
 * toNetFlowsChartConfigRecord
 * Converts a net flows config into the chart config record shape.
 */
export function toNetFlowsChartConfigRecord(
    config: FlowsChartConfig,
): ChartConfig {
    return {
        start_date: config.start_date,
        end_date: config.end_date,
        min_net_flow: config.min_net_flow,
        scale: config.scale,
        query: config.query,
        default_group_by: config.default_group_by,
        groups_by_options: config.groups_by_options,
    };
}
